import { useEffect, useMemo, useRef } from 'react'
import dayjs from 'dayjs'
import {
  useQuery,
  type QueryClient,
  useQueryClient
} from '@tanstack/react-query'
import {
  getOrganizationAiIntegrationUsageSnapshot,
  saveBackfillResult
} from '../apis/organizations'
import {
  backfillChanged,
  classifyBackfillOutcome
} from '../utils/aiBackfillOutcome'
import { queryKeys } from '../lib/queryKeys'
import { parseUtc } from '../utils/dateHelpers'
import type { AiBackfillResult } from '../types'

// Mirrors the backend's MAX_CAP_SECONDS (Lambda 900s + margin): past it an unmoved window is a real "No change".
export const JOB_MAX_SECONDS = 960

// Saves a before/after record to S3 and refreshes the queries that read it.
export const persistBackfillResult = async (
  queryClient: QueryClient,
  orgId: string | number,
  saId: string | number,
  jobsSuiteUuid: string,
  result: Partial<AiBackfillResult>
): Promise<void> => {
  const resultKey = queryKeys.organizations.aiBackfillResult(
    orgId,
    saId,
    jobsSuiteUuid
  )
  // Write the cache first: the dock shows the job settled before this save lands.
  queryClient.setQueryData<AiBackfillResult>(
    resultKey,
    old => old && { ...old, ...result }
  )
  try {
    await saveBackfillResult(orgId, saId, jobsSuiteUuid, result)
  } catch (e) {
    // Drop the optimistic write so the stored record is re-read and can be confirmed again.
    queryClient.invalidateQueries({ queryKey: resultKey })
    throw e
  }
  queryClient.invalidateQueries({
    queryKey: queryKeys.organizations.aiBackfillHistory(orgId)
  })
  queryClient.invalidateQueries({ queryKey: resultKey })
  queryClient.invalidateQueries({
    queryKey: queryKeys.organizations.aiIntegrations(orgId)
  })
}

// Re-reads a "not confirmed yet" record live and writes the settled result back to S3,
// so every viewer of the history sees it, not just whoever opened it.
export const useConfirmBackfillResult = (
  record: AiBackfillResult | undefined,
  enabled: boolean
): Pick<AiBackfillResult, 'after' | 'outcome'> => {
  const queryClient = useQueryClient()
  const savedRef = useRef<string | null>(null)
  const pending = enabled && record?.outcome?.outcome === 'unconfirmed'
  // gcTime 0: no cached snapshot survives a close, so a re-open always reads fresh data.
  const { data: live, isFetching } = useQuery({
    queryKey: ['ai-backfill-confirm', record?.jobs_suite_uuid],
    queryFn: async () => {
      const { data } = await getOrganizationAiIntegrationUsageSnapshot(
        record?.organization_id ?? '',
        record?.sensor_authorization_id ?? '',
        record?.since ?? '',
        record?.until ?? ''
      )
      return data
    },
    enabled: pending,
    staleTime: 0,
    gcTime: 0
  })

  const { settled, outcome } = useMemo(() => {
    // A disabled query keeps its last data, so a settled record must not be re-judged by it.
    if (!pending || !live || !record) {
      return { settled: false, outcome: record?.outcome }
    }
    const before = record.before ?? undefined
    const startedAt = parseUtc(record.created_at).subtract(
      record.duration_seconds ?? 0,
      'second'
    )
    const isSettled =
      (!!before && backfillChanged(before, live)) ||
      dayjs().diff(startedAt, 'second') >= JOB_MAX_SECONDS
    return {
      settled: isSettled,
      outcome: classifyBackfillOutcome(before, live, !isSettled)
    }
  }, [pending, live, record])

  useEffect(() => {
    const uuid = record?.jobs_suite_uuid
    const orgId = record?.organization_id
    const saId = record?.sensor_authorization_id
    // isFetching: a re-open still shows the previous snapshot until the fresh read lands.
    if (
      !live ||
      isFetching ||
      !settled ||
      !uuid ||
      orgId == null ||
      saId == null ||
      savedRef.current === uuid
    ) {
      return
    }
    savedRef.current = uuid
    const save = async (): Promise<void> => {
      try {
        await persistBackfillResult(queryClient, orgId, saId, uuid, {
          since: record.since,
          until: record.until,
          tool: record.tool,
          method: record.method,
          before: record.before,
          after: live,
          outcome,
          duration_seconds: record.duration_seconds
        })
      } catch {
        savedRef.current = null
      }
    }
    void save()
  }, [live, isFetching, record, settled, outcome, queryClient])

  return { after: (pending ? live : undefined) ?? record?.after, outcome }
}
